/**
 * Standalone import-and-allocate workflow (CR-6038).
 *
 * Legacy parity: the supplier downloads a template, populates it from their
 * ERP, uploads it, then in one place decides where each imported delivery
 * line goes — a new ASN, an existing draft ASN, or split across several of
 * the above. Sits next to the Create ASN button on the ASN tab so it isn't
 * buried inside the create-ASN wizard like the old CR-4888 entry point.
 */
import { useState, useEffect } from 'react';
import { Loader2, Download, X, CheckCircle, AlertCircle, Split, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { purchasingAPI, AdvancedShippingNotice } from '../../../lib/api';
import { toast } from 'sonner';
import { DeliveryLineOption } from './types';
import { SplitDeliveryLineDialog } from './SplitDeliveryLineDialog';
import FileDropZone from '../../common/FileDropZone';

interface ImportResult {
  total: number;
  imported: number;
  ignored: number;
  errors: number;
  error_details: Array<{ row: number; errors: string[] }>;
  delivery_line_ids: number[];
}

interface ImportAllocateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when user picks "Create new ASN" — parent opens the wizard prefilled. */
  onCreateNewASN: (deliveryLineIds: number[]) => void;
  /** Called whenever lines were attached to an existing ASN so the parent can refresh. */
  onAttached?: () => void;
}

type Step = 'upload' | 'allocate';

export function ImportAllocateModal({ isOpen, onClose, onCreateNewASN, onAttached }: ImportAllocateModalProps) {
  const [step, setStep] = useState<Step>('upload');

  // Step 1 state
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Step 2 state
  const [importedIds, setImportedIds] = useState<number[]>([]);
  const [lines, setLines] = useState<DeliveryLineOption[]>([]);
  const [draftASNs, setDraftASNs] = useState<AdvancedShippingNotice[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set());
  const [targetASN, setTargetASN] = useState<string>('new');
  const [allocating, setAllocating] = useState(false);
  const [splittingLine, setSplittingLine] = useState<DeliveryLineOption | null>(null);

  // Reset whenever the modal is re-opened.
  useEffect(() => {
    if (!isOpen) return;
    setStep('upload');
    setSelectedFile(null);
    setErrorMsg(null);
    setImportResult(null);
    setImportedIds([]);
    setLines([]);
    setSelectedLineIds(new Set());
    setTargetASN('new');
  }, [isOpen]);

  // Fetch the available delivery lines + 'created' status ASNs whenever we
  // enter the allocate step so the dropdown is fresh after each operation.
  // CR-6038: scoped to SupplierImport so the post-upload table only ever
  // surfaces rows from this workflow, never call-off-driven lines.
  const refreshAllocateData = async (ids: number[]) => {
    const [linesRes, asnsRes] = await Promise.all([
      purchasingAPI.getAvailableDeliveryLines({ source_type: 'SupplierImport' }),
      purchasingAPI.getASNs({ status: 'created', page_size: 100 }),
    ]);
    const allLines = (linesRes.data as unknown as DeliveryLineOption[]) || [];
    setLines(allLines.filter((l) => ids.includes(l.id)));
    setDraftASNs(asnsRes.data.results || []);
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await purchasingAPI.downloadASNTemplate();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asn_delivery_lines_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setErrorMsg(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await purchasingAPI.importASNLines(formData);
      const data = response.data;
      setImportResult(data);
      if (data.imported > 0) {
        toast.success(`${data.imported} delivery line${data.imported !== 1 ? 's' : ''} imported`);
        setImportedIds(data.delivery_line_ids);
        await refreshAllocateData(data.delivery_line_ids);
        setSelectedLineIds(new Set(data.delivery_line_ids));
        setStep('allocate');
      } else {
        toast.info('No delivery lines imported');
      }
    } catch (err: unknown) {
      // CR-6038: a 400 from /import-lines/ can carry row-level errors in
      // `errors: [{row, errors[]}]`. Surface those in the same panel that
      // shows skipped rows on a partial success so the supplier can see
      // exactly which row was rejected and why.
      const axiosErr = err as {
        response?: {
          data?: {
            error?: string;
            detail?: string;
            errors?: Array<{ row: number; errors: string[] }>;
            total?: number;
          };
        };
      };
      const data = axiosErr?.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        (err instanceof Error ? err.message : 'Upload failed.');
      setErrorMsg(msg);
      if (data?.errors && data.errors.length > 0) {
        setImportResult({
          total: data.total ?? data.errors.length,
          imported: 0,
          ignored: 0,
          errors: data.errors.length,
          error_details: data.errors,
          delivery_line_ids: [],
        });
      }
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const toggleLineSelection = (id: number) => {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedLineIds((prev) =>
      prev.size === lines.length ? new Set() : new Set(lines.map((l) => l.id))
    );
  };

  const handleAllocate = async () => {
    const selected = Array.from(selectedLineIds);
    if (selected.length === 0) {
      toast.error('Select at least one delivery line to allocate.');
      return;
    }
    if (targetASN === 'new') {
      onCreateNewASN(selected);
      onClose();
      return;
    }
    setAllocating(true);
    try {
      const asnId = Number(targetASN);
      const response = await purchasingAPI.attachDeliveryLinesToASN(asnId, selected);
      toast.success(`${response.data.attached} line(s) attached to ASN.`);
      const remaining = importedIds.filter((id) => !selected.includes(id));
      setImportedIds(remaining);
      onAttached?.();
      if (remaining.length === 0) {
        onClose();
      } else {
        await refreshAllocateData(remaining);
        setSelectedLineIds(new Set());
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      toast.error(axiosErr?.response?.data?.error || 'Failed to attach lines.');
    } finally {
      setAllocating(false);
    }
  };

  const handleSplit = async (originalId: number, newIds: number[]) => {
    const next = importedIds.filter((id) => id !== originalId).concat(newIds);
    setImportedIds(next);
    await refreshAllocateData(next);
    // Pre-select the new child rows so the user can immediately allocate them.
    setSelectedLineIds(new Set(newIds));
    setSplittingLine(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Import & Allocate Delivery Lines</DialogTitle>
          <DialogDescription>
            {step === 'upload'
              ? 'Download the template, populate it from your ERP, then upload to create delivery lines.'
              : 'Allocate imported lines to a new or existing ASN. Split a line to send portions to different shipments.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 py-2">
            <div className="rounded-md bg-muted/50 border border-border px-4 py-3 text-sm space-y-1">
              <p>
                <span className="font-medium">Step 1:</span> Download the template.
              </p>
              <p>
                <span className="font-medium">Step 2:</span> Fill
                <span className="font-semibold"> PO_Number</span>,
                <span className="font-semibold"> Line</span>,
                <span className="font-semibold"> Qty_Released</span>, and
                <span className="font-semibold"> Delivery_Number</span> at minimum.
                Optional traceability columns:
                <span className="font-semibold"> Date_Released</span>,
                <span className="font-semibold"> CfC_Number</span>,
                <span className="font-semibold"> Batch_Number</span>,
                <span className="font-semibold"> Serial_Number</span>.
              </p>
              <p>
                <span className="font-medium">Step 3:</span> Upload — then assign each line
                to a new ASN, an existing draft ASN, or split it across shipments.
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                disabled={downloading}
                className="flex items-center gap-2"
              >
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download Template
              </Button>
            </div>

            {/* Customer review 11 Sep (drag and drop everywhere). */}
            <FileDropZone
              size="compact"
              accept=".xlsx,.xls,.csv"
              disabled={uploading}
              hint="Excel (.xlsx, .xls) or CSV"
              onFiles={(files) => { setSelectedFile(files[0]); setErrorMsg(null); }}
              data-testid="import-allocate-drop-zone"
            />

            {selectedFile && (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <span className="font-medium truncate">{selectedFile.name}</span>
                <div className="flex items-center gap-2 ml-2 shrink-0">
                  <Button size="sm" onClick={handleUpload} disabled={uploading}>
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        Uploading…
                      </>
                    ) : (
                      'Import'
                    )}
                  </Button>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="p-0.5 hover:bg-muted rounded"
                    aria-label="Clear file"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                {errorMsg}
              </div>
            )}

            {importResult && importResult.error_details.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <div className="text-xs font-medium text-amber-800 mb-1">Skipped rows:</div>
                <ul className="text-xs text-amber-900 space-y-0.5 max-h-32 overflow-y-auto">
                  {importResult.error_details.map((e) => (
                    <li key={e.row}>
                      Row {e.row}: {e.errors.join('; ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {step === 'allocate' && (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-900">
              <CheckCircle className="h-4 w-4" />
              {importedIds.length} delivery line{importedIds.length !== 1 ? 's' : ''} imported and ready to allocate.
            </div>

            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border">
                    <th className="text-left px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={lines.length > 0 && selectedLineIds.size === lines.length}
                        onChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-medium">Delivery #</th>
                    <th className="text-left px-3 py-2 font-medium">Part</th>
                    <th className="text-left px-3 py-2 font-medium">PO</th>
                    <th className="text-right px-3 py-2 font-medium">Qty</th>
                    <th className="text-right px-3 py-2 font-medium w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                        No lines remain to allocate.
                      </td>
                    </tr>
                  ) : (
                    lines.map((line) => (
                      <tr key={line.id} className="border-b border-border last:border-b-0 hover:bg-muted/30">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedLineIds.has(line.id)}
                            onChange={() => toggleLineSelection(line.id)}
                            aria-label={`Select ${line.delivery_number}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{line.delivery_number}</td>
                        <td className="px-3 py-2">{line.part_number}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {line.po_order_number || '—'}
                          {line.po_line_number != null ? ` / L${line.po_line_number}` : ''}
                        </td>
                        <td className="px-3 py-2 text-right">{line.qty_released}</td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSplittingLine(line)}
                            className="h-7 px-2 text-xs"
                          >
                            <Split className="h-3 w-3 mr-1" />
                            Split
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-end justify-between pt-2 border-t border-border">
              <div className="space-y-1">
                <label className="block text-xs font-medium text-muted-foreground">
                  Allocate {selectedLineIds.size} selected line{selectedLineIds.size !== 1 ? 's' : ''} to
                </label>
                <select
                  value={targetASN}
                  onChange={(e) => setTargetASN(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm min-w-[260px]"
                >
                  <option value="new">+ Create a new ASN</option>
                  {draftASNs.map((asn) => (
                    <option key={asn.id} value={String(asn.id)}>
                      {asn.asn_number} — {asn.customer_name || asn.customer_details?.name || 'Unknown customer'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onClose} disabled={allocating}>
                  Done
                </Button>
                <Button
                  size="sm"
                  onClick={handleAllocate}
                  disabled={allocating || selectedLineIds.size === 0}
                  className="flex items-center gap-2"
                >
                  {allocating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Allocate
                </Button>
              </div>
            </div>
          </div>
        )}

        <SplitDeliveryLineDialog
          line={splittingLine}
          onClose={() => setSplittingLine(null)}
          onSplit={handleSplit}
        />
      </DialogContent>
    </Dialog>
  );
}
